import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  anxietyAssessments: [],
  currentAnxietyAssessment: null,
  anxietySymptomsMaster: [], // For dropdowns
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Anxiety Assessments (optionally by patient ID)
export const fetchAnxietyAssessments = createAsyncThunk(
  'anxiety/fetchAnxietyAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/anxiety-assessments?patient=${patientId}`
        : '/anxiety-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch anxiety assessments'
      );
    }
  }
);

// Fetch a single Anxiety Assessment
export const fetchAnxietyAssessment = createAsyncThunk(
  'anxiety/fetchAnxietyAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/anxiety-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch anxiety assessment'
      );
    }
  }
);

// Create a new Anxiety Assessment
export const createAnxietyAssessment = createAsyncThunk(
  'anxiety/createAnxietyAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/anxiety-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create anxiety assessment'
      );
    }
  }
);

// Update an existing Anxiety Assessment
export const updateAnxietyAssessment = createAsyncThunk(
  'anxiety/updateAnxietyAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/anxiety-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update anxiety assessment'
      );
    }
  }
);

// Delete an Anxiety Assessment
export const deleteAnxietyAssessment = createAsyncThunk(
  'anxiety/deleteAnxietyAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/anxiety-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete anxiety assessment'
      );
    }
  }
);

// Fetch Master Anxiety Symptoms for dropdowns
export const fetchAnxietySymptomsMaster = createAsyncThunk(
  'anxiety/fetchAnxietySymptomsMaster',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/symptoms');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch anxiety symptoms master'
      );
    }
  }
);

// ==================== Slice ====================
const anxietySlice = createSlice({
  name: 'anxiety',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAnxietyAssessment: (state, action) => {
      state.currentAnxietyAssessment = action.payload;
    },
    clearCurrentAnxietyAssessment: (state) => {
      state.currentAnxietyAssessment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchAnxietyAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnxietyAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.anxietyAssessments = action.payload;
      })
      .addCase(fetchAnxietyAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchAnxietyAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnxietyAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAnxietyAssessment = action.payload;
      })
      .addCase(fetchAnxietyAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createAnxietyAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAnxietyAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.anxietyAssessments.unshift(action.payload);
      })
      .addCase(createAnxietyAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateAnxietyAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAnxietyAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.anxietyAssessments.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.anxietyAssessments[index] = updated;
        }

        if (state.currentAnxietyAssessment?.id === updated.id) {
          state.currentAnxietyAssessment = updated;
        }
      })
      .addCase(updateAnxietyAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteAnxietyAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAnxietyAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.anxietyAssessments = state.anxietyAssessments.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentAnxietyAssessment?.id === action.payload) {
          state.currentAnxietyAssessment = null;
        }
      })
      .addCase(deleteAnxietyAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SYMPTOMS MASTER
      .addCase(fetchAnxietySymptomsMaster.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnxietySymptomsMaster.fulfilled, (state, action) => {
        state.isLoading = false;
        state.anxietySymptomsMaster = action.payload;
      })
      .addCase(fetchAnxietySymptomsMaster.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentAnxietyAssessment,
  clearCurrentAnxietyAssessment,
} = anxietySlice.actions;

export default anxietySlice.reducer;
